/* eslint-disable lines-between-class-members */
import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { A } from '@ember/array';
import './styles.scss';
import AccountApi from 'mewe/api/account-api';
import FunctionalUtils from 'mewe/shared/functional-utils.js';
import GroupStore from 'mewe/stores/group-store';
import Session from 'mewe/shared/session';
import { inject as service } from '@ember/service';
import dispatcher from 'mewe/dispatcher';
import { later } from '@ember/runloop';
export default class DeleteAccountDialog extends Component {
  @service router;
  @service dynamicDialogs;
  @service account;

  @tracked isLoading = true;
  @tracked confirmError;
  @tracked step = 0;
  @tracked ownedGroups = A();
  @tracked deleteAllGroups = true;
  @tracked selectGroupToTransfer = false;
  @tracked otpBtnDisabled = false;
  @tracked _isNextStepDisabled;
  @tracked otpTimeout;
  @tracked otpSent;
  @tracked otpCode = '';
  @tracked otpDestinations = new Set();
  @tracked otpSelectedDestination;
  @tracked isConfirmChecked = false;
  @tracked validate = {
    password: {
      isValid: true,
      value: '',
    },
  };

  deleteReasons = [
    {
      value: __("I don't have enough time for MeWe"),
      for: 'no-time',
      checked: false,
    },
    {
      value: __('The site is too confusing'),
      for: 'confusing',
      checked: false,
    },
    {
      value: __("I don't have any friends here"),
      for: 'no-friends',
      checked: false,
    },
    {
      value: __('I already have programs/services that provide these functions'),
      for: 'other-programs',
      checked: false,
    },
    {
      value: __("It doesn't work with my browser"),
      for: 'browser',
      checked: false,
    },
    {
      value: __("It doesn't work with my mobile phone"),
      for: 'phone',
      checked: false,
    },
  ];

  constructor() {
    super(...arguments);

    this.ownedGroups = A(
      GroupStore.getState().confirmedGroups.filter((g) => {
        return g.role === 'Owner';
      })
    );
  }

  @action
  async onInsert() {
    try {
      const { phoneNumbers, emails, primaryEmail } = await AccountApi.getOtpDestinations();

      this.otpDestinations = new Set(
        typeof primaryEmail === 'undefined'
          ? [...phoneNumbers, ...emails]
          : [`${primaryEmail}`, ...phoneNumbers, ...emails]
      );

      if (this.otpDestinations.size === 1) {
        const [first] = this.otpDestinations;
        this.otpSelectedDestination = first;
      }

      this.isLoading = false;
    } catch (e) {
      this.isLoading = false;
    }
  }

  get additionalDeleteText() {
    return this.args.compArgs.additionalDeleteText;
  }

  get noRedirectionForGroupTransfering() {
    return this.args.compArgs.noRedirectionForGroupTransfering;
  }

  get getDeleteButtonText() {
    if (this.otpDestinations.size > 0) {
      return __('Send verification code');
    }

    return __('Delete account');
  }

  get dialogHeaderText() {
    if (this.step === 2) {
      return __('We will miss you');
    }

    return __('Delete Account');
  }

  // for web3 users show password input only after sending OTP code
  get showPasswordInput() {
    return !this.account.activeUser?.dsnpHandle || this.otpSelectedDestination;
  }

  get isNextStepDisabled() {
    const ownedGroup = this.deleteAllGroups || !this.ownedGroups.length;
    const checkBoxChecked = this.isConfirmChecked;
    const hasDestinationSelected = this.otpDestinations.size > 0 ? Boolean(this.otpSelectedDestination) : true;

    return !(checkBoxChecked && ownedGroup & hasDestinationSelected);
  }

  get displayCenterVariant() {
    return !this.ownedGroups.length && this.otpDestinations.size <= 1;
  }

  @action
  checkReasonInput(reason) {
    set(reason, 'checked', !reason.checked);
  }

  @action
  nextStep() {
    this.step = this.step + 1;

    if (this.step === 1 && this.otpDestinations.size > 0) {
      this.sendOtpCode();
    }
  }

  @action
  selectOtpDestination(destination) {
    this.otpSelectedDestination = destination;
  }

  @action
  enterGroupDataView(group) {
    // SG-22931
    // when delete dialog opened from accept TermsOfService dialog then can't redirect to group settings, open transfer ownership dialog directly
    if (this.noRedirectionForGroupTransfering) {
      const groupTransferedCallback = () => {
        GroupStore.send('removeGroup', group.id);
        this.ownedGroups.removeObject(group);
      };
      dispatcher.dispatch('group', 'selectNewOwner', group, groupTransferedCallback);
    } else {
      this.router.transitionTo('app.group.settings', group.id);
      this.closeDialog();
    }
  }

  @action
  closeDialog() {
    this.dynamicDialogs.close(this.args.compArgs.dialogId);
  }

  @action
  async deleteAccount() {
    const deleteReasons = this.deleteReasons.filter((reason) => reason.checked).map((reason) => reason.value);
    const params = {
      deleteReasons,
      otherReason: this.otherDeleteReason ? this.otherDeleteReason.trim() : '',
    };

    if (this.account.activeUser?.dsnpHandle) {
      params.otp = this.otpCode;
    } else {
      params.password = this.validate.password.value;
    }

    try {
      await AccountApi.deleteAccount(params);
      FunctionalUtils.info(__('Account has been successfully deleted.'));
      Session.clearLocalData();
      setTimeout(() => (location.href = '/'), 800); // give user some time to see the msg
    } catch (e) {
      if (this.otpCode) {
        this.otpCode = '';
      }

      this.step = 1;
      this.deleteReasons.forEach((reason) => (reason.checked = false));
      this.confirmError = __(
        `Please enter a valid ${this.account.activeUser?.dsnpHandle ? 'verification code' : 'password'}.`
      );
    }
  }

  @action
  async sendOtpCode() {
    this.otpBtnDisabled = true;

    try {
      // web3 users don't have password, we send OTP instead
      await AccountApi.sendOtpCode(this.otpSelectedDestination);

      /* 10 seconds timeout to prevent abusing OTP */
      this.otpTimeout = setTimeout(() => (this.otpBtnDisabled = false), 10000);
    } catch (e) {
      FunctionalUtils.error(__(`Something went wrong while sending code. Please try again later.`));
      this.otpSent = false;
      this.otpBtnDisabled = false;
      clearTimeout(this.otpTimeout);
    }
  }

  @action
  validatePassword() {
    this.validate = {
      ...this.validate,
      password: {
        ...this.validate.password,
        isValid: this.validate.password.value.length >= 6,
      },
    };
  }

  @action
  updateSelected(type) {
    if (type === 'delete' && !this.deleteAllGroups) {
      this.deleteAllGroups = true;
      this.selectGroupToTransfer = false;
    } else if (type === 'transfer' && !this.selectGroupToTransfer) {
      this.deleteAllGroups = false;
      this.selectGroupToTransfer = true;
    }
  }

  @action
  checkAutofill() {
    later(() => {
      const autofilledInput = document.querySelector('input:-webkit-autofill');

      if (autofilledInput) {
        this.validate = {
          ...this.validate,
          password: {
            isValid: autofilledInput.value.length >= 6,
            value: autofilledInput.value,
          },
        };
      }
    });
  }
}
